import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsAPI } from '../api';
import toast from 'react-hot-toast';
import { Search, Eye, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Patients() {
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 10;

    const fetchPatients = () => {
        setLoading(true);
        patientsAPI
            .getAll({ page, limit, search, status })
            .then((res) => {
                setPatients(res.data.data.patients);
                setTotal(res.data.data.total);
            })
            .catch(() => toast.error('Failed to load patients'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchPatients();
    }, [page, status]);
    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            fetchPatients();
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this patient?')) return;
        try {
            await patientsAPI.delete(id);
            toast.success('Patient deleted');
            fetchPatients();
        } catch {
            toast.error('Failed to delete');
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await patientsAPI.updateStatus(id, newStatus);
            toast.success('Status updated');
            fetchPatients();
        } catch {
            toast.error('Failed to update');
        }
    };

    const pages = Math.ceil(total / limit);

    return (
        <div className="fade-in">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Patients</h1>
                    <p className="page-subtitle">
                        {total} total patients registered
                    </p>
                </div>
            </div>

            <div className="filters">
                <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
                    <Search size={16} />
                    <input
                        className="input"
                        placeholder="Search by name, email, phone..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className="input"
                    value={status}
                    onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                    }}
                >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                </select>
            </div>

            <div className="card" style={{ padding: 0 }}>
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Patient</th>
                                <th>Contact</th>
                                <th>Age / Gender</th>
                                <th>Verified</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="loading-container">
                                            <div className="spinner" />
                                        </div>
                                    </td>
                                </tr>
                            ) : patients.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={7}
                                        style={{
                                            textAlign: 'center',
                                            color: 'var(--text3)',
                                            padding: 60,
                                        }}
                                    >
                                        No patients found
                                    </td>
                                </tr>
                            ) : (
                                patients.map((p) => (
                                    <tr key={p.patient_id}>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                }}
                                            >
                                                <div className="avatar">
                                                    {(p.full_name || 'P')[0]}
                                                </div>
                                                <div>
                                                    <div
                                                        style={{
                                                            color: 'var(--text)',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {p.full_name}
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            color: 'var(--text3)',
                                                        }}
                                                    >
                                                        {p.profile_id}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 13 }}>
                                                {p.email}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: 'var(--text3)',
                                                }}
                                            >
                                                {p.country_code}{' '}
                                                {p.mobile_number}
                                            </div>
                                        </td>
                                        <td>
                                            {p.age || '—'} / {p.gender || '—'}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${p.is_verified === 'Yes' ? 'badge-success' : 'badge-warning'}`}
                                            >
                                                {p.is_verified === 'Yes'
                                                    ? 'Verified'
                                                    : 'Pending'}
                                            </span>
                                        </td>
                                        <td>
                                            <select
                                                value={p.status}
                                                onChange={(e) =>
                                                    handleStatusChange(
                                                        p.patient_id,
                                                        e.target.value
                                                    )
                                                }
                                                style={{
                                                    background:
                                                        'var(--surface2)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: 8,
                                                    color: 'var(--text)',
                                                    fontSize: 13,
                                                    padding: '4px 8px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <option value="Active">
                                                    Active
                                                </option>
                                                <option value="Inactive">
                                                    Inactive
                                                </option>
                                            </select>
                                        </td>
                                        <td style={{ fontSize: 13 }}>
                                            {new Date(
                                                p.created_at
                                            ).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: 8,
                                                }}
                                            >
                                                <button
                                                    className="btn btn-ghost btn-sm"
                                                    onClick={() =>
                                                        navigate(
                                                            `/patients/${p.patient_id}`
                                                        )
                                                    }
                                                >
                                                    <Eye size={14} />
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() =>
                                                        handleDelete(
                                                            p.patient_id
                                                        )
                                                    }
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {pages > 1 && (
                    <div
                        style={{
                            padding: '16px 20px',
                            borderTop: '1px solid var(--border)',
                        }}
                    >
                        <div className="pagination">
                            <button
                                className="page-btn"
                                onClick={() => setPage((p) => p - 1)}
                                disabled={page === 1}
                            >
                                <ChevronLeft size={14} />
                            </button>
                            {Array.from(
                                { length: Math.min(pages, 5) },
                                (_, i) => i + 1
                            ).map((p) => (
                                <button
                                    key={p}
                                    className={`page-btn ${page === p ? 'active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="page-btn"
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page === pages}
                            >
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
